/**
 * Action Modal Content Service
 *
 * Unified service for fetching action modal content.
 * Routes to appropriate system based on civic level:
 * - Observer: Use existing modal_blocks system (unchanged)
 * - Other levels: Use new song_modal_blocks table
 */

import { supabase } from './supabaseClient';
import type {
  ActionModalBlock,
  ActionModalLevelKey,
  ActionModalSectionKey,
  ActionModalsByLevel,
  civicLevelToKey,
  modalTypeToSectionKey,
} from './actionModalBlockTypes';
import { getPlaceholderBlocks, ensureDefaults } from './actionModalDefaults';
import { getModalContent, ModalBlock } from './supabase/modalContent';
import { fetchModalBlocks, fetchPathwayCards, getCivicLevelBySlug } from './actionModalProductionService';
import { convertModalBlockToFrontend, convertPathwayCardsToBlocks } from './actionModalBlockConverter';

// Import the helper functions
import { civicLevelToKey as _civicLevelToKey, modalTypeToSectionKey as _modalTypeToSectionKey } from './actionModalBlockTypes';

// Result type that can be either system
export type ActionModalContentResult =
  | { system: 'legacy'; blocks: ModalBlock[] }
  | { system: 'new'; blocks: ActionModalBlock[] };

// Map modal keys to section keys (supports both camelCase and snake_case)
const modalKeyMap: Record<string, 'look_deeper' | 'one_key_insight' | 'explore_connections' | 'take_it_further'> = {
  'lookDeeper': 'look_deeper',
  'look_deeper': 'look_deeper',
  'oneKeyInsight': 'one_key_insight',
  'one_key_insight': 'one_key_insight',
  'exploreConnections': 'explore_connections',
  'explore_connections': 'explore_connections',
  'takeItFurther': 'take_it_further',
  'take_it_further': 'take_it_further',
};

/**
 * Get action modal content for a song based on civic level
 * Routes to appropriate system automatically
 * Falls back to legacy content if level-specific content is placeholder-only
 */
export async function getActionModalContent(
  songId: string,
  civicLevel: string,
  modalType: string,
  includeDrafts = false
): Promise<ActionModalContentResult> {
  const levelKey = _civicLevelToKey(civicLevel);

  // Observer always uses the legacy modal_blocks system
  if (levelKey === 'observer') {
    const legacyBlocks = await getModalContent(songId, levelKey, modalType as any);
    return { system: 'legacy', blocks: legacyBlocks };
  }

  // Other levels use the new song_modal_blocks table
  const modalKey = modalKeyMap[modalType] || 'look_deeper';

  // Special case: Take It Further uses pathway cards
  if (modalKey === 'take_it_further') {
    const pathwayCards = await fetchPathwayCards({
      songId,
      civicLevelSlug: levelKey,
      includeDrafts,
    });
    const blocks = convertPathwayCardsToBlocks(pathwayCards);
    return { system: 'new', blocks };
  }

  const newBlocks = await getActionModalsByLevelProduction(songId, levelKey, modalKey, includeDrafts);

  // Use new blocks (including placeholder content for level-specific experience)
  return { system: 'new', blocks: newBlocks };
}

/**
 * Fetch action modals by level from song_modal_blocks table
 */
async function getActionModalsByLevelProduction(
  songId: string,
  levelKey: ActionModalLevelKey,
  modalKey: 'look_deeper' | 'one_key_insight' | 'explore_connections' | 'take_it_further',
  includeDrafts = false
): Promise<ActionModalBlock[]> {
  try {
    const blocks = await fetchModalBlocks({
      songId,
      civicLevelSlug: levelKey,
      modalKey,
      includeDrafts,
    });

    // Convert production blocks to ActionModalBlock format using the converter
    const convertedBlocks: ActionModalBlock[] = blocks
      .map(block => convertModalBlockToFrontend(block))
      .filter((block): block is ActionModalBlock => block !== null);

    if (convertedBlocks.length === 0) {
      // No content yet, return placeholder
      const sectionKey = _modalTypeToSectionKey(modalKey);
      return getPlaceholderBlocks()[sectionKey].blocks;
    }

    return convertedBlocks;
  } catch (err) {
    console.error('Unexpected error in getActionModalsByLevelProduction:', err);
    const sectionKey = _modalTypeToSectionKey(modalKey);
    return getPlaceholderBlocks()[sectionKey].blocks;
  }
}

/**
 * Save action modals by level to song's JSONB field
 */
export async function saveActionModalsByLevel(
  songId: string,
  actionModalsByLevel: ActionModalsByLevel
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('songs')
      .update({ action_modals_by_level: actionModalsByLevel })
      .eq('id', songId);

    if (error) {
      console.error('Error saving action_modals_by_level:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Unexpected error in saveActionModalsByLevel:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Get full action modals by level structure for a song (for admin editing)
 */
export async function getFullActionModalsByLevel(
  songId: string
): Promise<ActionModalsByLevel> {
  try {
    const { data: song, error } = await supabase
      .from('songs')
      .select('action_modals_by_level')
      .eq('id', songId)
      .maybeSingle();

    if (error || !song) {
      // Return defaults if no content exists
      return ensureDefaults({});
    }

    const actionModalsByLevel = (song.action_modals_by_level as ActionModalsByLevel) || {};
    return ensureDefaults(actionModalsByLevel);
  } catch (err) {
    console.error('Error in getFullActionModalsByLevel:', err);
    return ensureDefaults({});
  }
}
